import { useMutation } from "@tanstack/react-query";
import {
  getApiUrl,
  getMerchantsPublicIDTerminalContractsTerminalContractPublicIDContractFilePath,
} from "Infrastructure/Api/Api";
import { JWT_KEY } from "Utils/AuthUtils";
import { getLocalStorageItem } from "Utils/Storage/LocalStorage/SecureLocalStorage";
import { format } from "date-fns";
import FileSaver from "file-saver";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";

type Params = {
  publicID: string;
  terminalContractPublicID: string;
};

const fetchTerminalContract = async ({
  publicID,
  terminalContractPublicID,
}: Params) => {
  const route = `${getApiUrl()}${getMerchantsPublicIDTerminalContractsTerminalContractPublicIDContractFilePath(publicID, terminalContractPublicID)}`;
  const response = await fetch(route, {
    method: "GET",
    mode: "cors",
    headers: { Authorization: getLocalStorageItem(JWT_KEY) ?? "" },
  });

  // Without this a 4xx/5xx would resolve and the base64 decode below would
  // choke on the error body.
  if (!response.ok) {
    throw new Error(
      `Terminal contract download failed with status ${response.status}`,
    );
  }

  return response;
};

export const useDownloadTerminalContract = () => {
  const { t } = useResource();

  return useMutation({
    mutationFn: async (params: Params) => {
      const response = await fetchTerminalContract(params);
      const data = await response.json();

      // Convert base64 to blob
      const byteCharacters = atob(data.bytes);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      const fileName = `${t(Resources.Hooks.TerminalContract.UcbContractPrefix)}${format(new Date(), "yyyy-MM-dd")}.pdf`;
      FileSaver.saveAs(blob, fileName);
    },
    // Failures reject instead of being swallowed, so the global MutationCache
    // handler sees them; the user feedback stays here.
    onError: () =>
      toast.error(t(Resources.Hooks.TerminalContract.DownloadError)),
  });
};
